'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketActionReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketActionReviewDialogV2';
import { MarketSummaryCard } from '@/components/MarketPage/MarketSimpleActionPanel/MarketSimpleActionPanelClient/MarketOperationPanel/MarketSummary';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import { getSupplyBorrowOperationLabel } from '@/helpers/operationLabels';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { computeLtv, computePreviewFinalPosition } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionError } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketCollateralNativeWrap } from '@/hooks/operation/market/v2/useMarketCollateralNativeWrap';
import { useMarketMaxBorrowV2 } from '@/hooks/operation/market/v2/useMarketMaxBorrowV2';
import { useMarketReallocations } from '@/hooks/operation/market/v2/useMarketReallocations';
import usePositionManagerMarketDialogLabels from '@/hooks/operation/position-manager/usePositionManagerMarketDialogLabels';
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain';
import useBundleCanTransferOnchain, { isMarketBundleBlocked } from '@/hooks/operation/useBundleCanTransferOnchain';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketBorrowCompletedParams, trackMarketBorrowCompleted } from '@/lib/analytics/client/trackers/market-borrow';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { useBorrowDialogData } from '../useMarketDialogData';
import PositionManagerBorrowDialogLoading from './PositionManagerBorrowDialogLoading';

/** Debounce window (ms) before an edited amount drives the local build (validation only). */
const PREVIEW_DEBOUNCE_MS = 400;
interface PositionManagerBorrowDialogProps {
  marketId: MarketId;
  chainId: number;
}
function PositionManagerBorrowDialog(t0) {
  const $ = _c(185);
  const {
    marketId,
    chainId
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const {
    marketSupplyBorrow
  } = useFeatureFlags();
  const [inputCollateralAmount, setInputCollateralAmount] = useState(ZERO);
  const [inputLoanAmount, setInputLoanAmount] = useState(ZERO);
  const [isCollateralInputPending, setIsCollateralInputPending] = useState(false);
  const [isLoanInputPending, setIsLoanInputPending] = useState(false);
  let t1;
  if ($[0] !== chainId || $[1] !== marketId) {
    t1 = {
      chainId,
      marketId
    };
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: dialogData,
    isPending: queryIsLoading,
    error: queryError
  } = useBorrowDialogData(t1);
  dialogData?.loanAsset;
  const t2 = dialogData?.loanAsset;
  let t3;
  if ($[3] !== t2) {
    t3 = parseApiAsset(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const loanAsset = t3;
  let t4;
  if ($[5] !== dialogData?.collateralAsset) {
    t4 = () => parseApiAsset(dialogData?.collateralAsset);
    $[5] = dialogData?.collateralAsset;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  dialogData?.collateralAsset;
  let t5;
  if ($[7] !== t4) {
    t5 = t4();
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const collateralAsset = t5;
  const marketParams = dialogData?.marketParams;
  const isMarketSupplyBorrowEnabled = marketSupplyBorrow && !!marketParams;
  const t6 = dialogData?.collateralAsset?.address;
  const t7 = dialogData?.loanAsset.address;
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = [t6, t7];
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== chainId || $[13] !== t8) {
    t9 = {
      chainId,
      assetAddresses: t8
    };
    $[12] = chainId;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const geoRestriction = useMarketGeoRestriction(t9);
  let t10;
  if ($[15] !== isMarketSupplyBorrowEnabled || $[16] !== marketId || $[17] !== targetChain.id || $[18] !== targetChain.name) {
    t10 = {
      entryPoint: "position_manager",
      action: "supply_borrow",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[15] = isMarketSupplyBorrowEnabled;
    $[16] = marketId;
    $[17] = targetChain.id;
    $[18] = targetChain.name;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  useTrackMarketActionsV2EntryViewed(t10);
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading
  } = useMarketBlue(marketParams);
  const t11 = collateralAsset?.address;
  let t12;
  if ($[20] !== inputCollateralAmount || $[21] !== isMarketSupplyBorrowEnabled || $[22] !== t11) {
    t12 = {
      collateralToken: t11,
      totalCollateral: inputCollateralAmount,
      enabled: isMarketSupplyBorrowEnabled,
      wrapEnabled: isMarketSupplyBorrowEnabled
    };
    $[20] = inputCollateralAmount;
    $[21] = isMarketSupplyBorrowEnabled;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const {
    isWNativeCollateral,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    maxCollateral,
    isMaxLoading: isMaxCollateralLoading,
    isWrappingToggleLoading,
    wrapErrorMessage
  } = useMarketCollateralNativeWrap(t12);
  let t13;
  if ($[24] !== maxCollateral) {
    t13 = value => {
      if (value && isDefined(maxCollateral)) {
        setInputCollateralAmount(maxCollateral);
      }
    };
    $[24] = maxCollateral;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const onMaxCollateralClick = t13;
  let t14;
  if ($[26] !== inputCollateralAmount || $[27] !== isMarketSupplyBorrowEnabled || $[28] !== maxCollateral) {
    t14 = isMarketSupplyBorrowEnabled && isDefined(maxCollateral) && maxCollateral > ZERO && inputCollateralAmount === maxCollateral;
    $[26] = inputCollateralAmount;
    $[27] = isMarketSupplyBorrowEnabled;
    $[28] = maxCollateral;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const isMaxCollateral = t14;
  let t15;
  if ($[30] !== inputCollateralAmount || $[31] !== isMarketSupplyBorrowEnabled || $[32] !== maxCollateral) {
    t15 = isMarketSupplyBorrowEnabled && isDefined(maxCollateral) && inputCollateralAmount > maxCollateral;
    $[30] = inputCollateralAmount;
    $[31] = isMarketSupplyBorrowEnabled;
    $[32] = maxCollateral;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const isCollateralOverMax = t15;
  let t16;
  if ($[34] !== inputLoanAmount || $[35] !== isMarketSupplyBorrowEnabled || $[36] !== marketParams) {
    t16 = {
      marketParams,
      operation: "borrow",
      amount: inputLoanAmount,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[34] = inputLoanAmount;
    $[35] = isMarketSupplyBorrowEnabled;
    $[36] = marketParams;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const {
    reallocations,
    maxReallocatable,
    isLoading: isReallocationLoading
  } = useMarketReallocations(t16);
  let t17;
  if ($[38] !== inputCollateralAmount || $[39] !== isMarketSupplyBorrowEnabled || $[40] !== marketParams || $[41] !== maxReallocatable) {
    t17 = {
      marketParams,
      proposedCollateral: inputCollateralAmount,
      maxReallocatable,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[38] = inputCollateralAmount;
    $[39] = isMarketSupplyBorrowEnabled;
    $[40] = marketParams;
    $[41] = maxReallocatable;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const {
    maxBorrow,
    maxBorrowCeiling,
    isMaxLoading: isMaxBorrowLoading
  } = useMarketMaxBorrowV2(t17);
  let t18;
  if ($[43] !== maxBorrow) {
    t18 = value_0 => {
      if (value_0 && isDefined(maxBorrow)) {
        setInputLoanAmount(maxBorrow);
      }
    };
    $[43] = maxBorrow;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const onMaxBorrowClick = t18;
  let t19;
  if ($[45] !== inputLoanAmount || $[46] !== isMarketSupplyBorrowEnabled || $[47] !== maxBorrow) {
    t19 = isMarketSupplyBorrowEnabled && isDefined(maxBorrow) && maxBorrow > ZERO && inputLoanAmount === maxBorrow;
    $[45] = inputLoanAmount;
    $[46] = isMarketSupplyBorrowEnabled;
    $[47] = maxBorrow;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const isMaxBorrow = t19;
  let t20;
  if ($[49] !== inputLoanAmount || $[50] !== isMarketSupplyBorrowEnabled || $[51] !== maxBorrowCeiling) {
    t20 = isMarketSupplyBorrowEnabled && isDefined(maxBorrowCeiling) && inputLoanAmount > maxBorrowCeiling;
    $[49] = inputLoanAmount;
    $[50] = isMarketSupplyBorrowEnabled;
    $[51] = maxBorrowCeiling;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const isBorrowOverMax = t20;
  const [debouncedCollateral] = useDebounce(inputCollateralAmount, PREVIEW_DEBOUNCE_MS);
  const [debouncedLoan] = useDebounce(inputLoanAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedCollateral !== inputCollateralAmount || debouncedLoan !== inputLoanAmount;
  let t21;
  if ($[53] !== debouncedCollateral || $[54] !== debouncedLoan || $[55] !== marketParams || $[56] !== nativeAmount || $[57] !== reallocations) {
    t21 = {
      marketParams,
      mode: "supplyBorrow",
      collateralAmount: debouncedCollateral,
      loanAmount: debouncedLoan,
      nativeAmount,
      reallocations,
      withRequirements: false
    };
    $[53] = debouncedCollateral;
    $[54] = debouncedLoan;
    $[55] = marketParams;
    $[56] = nativeAmount;
    $[57] = reallocations;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const {
    buildError
  } = useMarketActionBuilder(t21);
  let t22;
  if ($[59] !== positionData) {
    t22 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO
    } : undefined;
    $[59] = positionData;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  const current = t22;
  const isEmpty = inputCollateralAmount === ZERO && inputLoanAmount === ZERO;
  let t23;
  bb0: {
    if (isEmpty || !current) {
      let t24;
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = {};
        $[61] = t24;
      } else {
        t24 = $[61];
      }
      t23 = t24;
      break bb0;
    }
    let t24;
    if ($[62] !== current || $[63] !== inputCollateralAmount || $[64] !== inputLoanAmount) {
      t24 = computePreviewFinalPosition({
        current,
        collateralDelta: inputCollateralAmount,
        loanDelta: inputLoanAmount,
        mode: "supplyBorrow"
      });
      $[62] = current;
      $[63] = inputCollateralAmount;
      $[64] = inputLoanAmount;
      $[65] = t24;
    } else {
      t24 = $[65];
    }
    t23 = t24;
  }
  const after = t23;
  let t24;
  if ($[66] !== buildError || $[67] !== collateralAsset?.symbol || $[68] !== isBorrowOverMax || $[69] !== isCollateralOverMax || $[70] !== isDebouncing || $[71] !== isEmpty || $[72] !== wrapErrorMessage) {
    t24 = () => {
      if (wrapErrorMessage) {
        return wrapErrorMessage;
      }
      if (isBorrowOverMax) {
        return "Maximum borrowable exceeded";
      }
      return isEmpty || isDebouncing ? undefined : getMarketActionError({
        buildError,
        insufficientBalanceSymbol: isCollateralOverMax ? collateralAsset?.symbol : undefined
      });
    };
    $[66] = buildError;
    $[67] = collateralAsset?.symbol;
    $[68] = isBorrowOverMax;
    $[69] = isCollateralOverMax;
    $[70] = isDebouncing;
    $[71] = isEmpty;
    $[72] = wrapErrorMessage;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  collateralAsset?.symbol;
  let t25;
  if ($[74] !== t24) {
    t25 = t24();
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  const buildErrorMessage = t25;
  const canTransferCollateralAsset = useAssetCanTransferOnchain(account.address, collateralAsset?.address, targetChain.id);
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset?.address, targetChain.id);
  const {
    canTransfer: collateralBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", collateralAsset?.address, targetChain.id);
  const {
    canTransfer: loanBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  let t26;
  if ($[76] !== collateralBundleAuthorized || $[77] !== inputCollateralAmount || $[78] !== inputLoanAmount || $[79] !== loanBundleAuthorized) {
    t26 = isMarketBundleBlocked({
      legs: [{
        amount: inputCollateralAmount,
        authorized: collateralBundleAuthorized
      }, {
        amount: inputLoanAmount,
        authorized: loanBundleAuthorized
      }]
    });
    $[76] = collateralBundleAuthorized;
    $[77] = inputCollateralAmount;
    $[78] = inputLoanAmount;
    $[79] = loanBundleAuthorized;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  const isBundleNotAuthorized = t26;
  const isBorrowReallocationLoading = inputLoanAmount > ZERO && isReallocationLoading;
  const isConfirmDisabled = !isMarketSupplyBorrowEnabled || isEmpty || isPositionLoading || !!positionError || inputCollateralAmount > ZERO && isMaxCollateralLoading || isMaxBorrowLoading || isBorrowReallocationLoading || account.isWrongChain || !!buildErrorMessage || isCollateralOverMax || isBorrowOverMax || !canTransferCollateralAsset || !canTransferLoanAsset || isBundleNotAuthorized;
  let t27;
  if ($[81] !== inputCollateralAmount || $[82] !== inputLoanAmount) {
    t27 = getSupplyBorrowOperationLabel(inputCollateralAmount, inputLoanAmount);
    $[81] = inputCollateralAmount;
    $[82] = inputLoanAmount;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  const operationLabel = t27;
  let t28;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = [];
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== buildErrorMessage || $[86] !== inputCollateralAmount || $[87] !== inputLoanAmount || $[88] !== operationLabel) {
    t29 = {
      operationLabel,
      inputCollateralAmount,
      inputLoanAmount,
      isNew: true,
      localErrorMessage: buildErrorMessage,
      operations: t28,
      simulationError: undefined,
      blockError: null,
      isPending: false
    };
    $[85] = buildErrorMessage;
    $[86] = inputCollateralAmount;
    $[87] = inputLoanAmount;
    $[88] = operationLabel;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  const {
    confirmButtonLabel
  } = usePositionManagerMarketDialogLabels(t29);
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  const isButtonLoading = account.isConnected && (isPositionLoading || isReallocationLoading);
  let t30;
  if ($[90] !== confirmButtonLabel || $[91] !== geoRestriction || $[92] !== isButtonLoading || $[93] !== isConfirmDisabled || $[94] !== tooltipContent) {
    t30 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: isConfirmDisabled,
      isLoading: isButtonLoading,
      finalizeLabel: confirmButtonLabel,
      tooltipContent
    });
    $[90] = confirmButtonLabel;
    $[91] = geoRestriction;
    $[92] = isButtonLoading;
    $[93] = isConfirmDisabled;
    $[94] = tooltipContent;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[96] !== t30) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t30);
    $[96] = t30;
    $[97] = geoActionProps;
    $[98] = shouldBlockFinalize;
  } else {
    geoActionProps = $[97];
    shouldBlockFinalize = $[98];
  }
  let t31;
  if ($[99] !== account.isContract || $[100] !== account.isSafe || $[101] !== collateralAsset || $[102] !== dialogData?.avgApy || $[103] !== dialogData?.marketForRewards || $[104] !== inputCollateralAmount || $[105] !== inputLoanAmount || $[106] !== loanAsset || $[107] !== marketId || $[108] !== marketParams || $[109] !== nativeAmount || $[110] !== reallocations || $[111] !== shouldBlockFinalize || $[112] !== targetChain.id || $[113] !== targetChain.name || $[114] !== toggleDialog) {
    t31 = () => {
      if (!marketParams || !loanAsset || !collateralAsset || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "position_manager",
        action: "supply_borrow",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name
      });
      toggleDialog(<MarketActionReviewDialogV2 marketParams={marketParams} marketId={marketId} mode="supplyBorrow" collateralAmount={inputCollateralAmount} loanAmount={inputLoanAmount} nativeAmount={nativeAmount} reallocations={reallocations} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={marketParams.lltv} marketForRewards={dialogData?.marketForRewards} avgBorrowApy={dialogData?.avgApy} onTxSuccess={() => {
        const params = buildMarketBorrowCompletedParams({
          marketId,
          loanAsset,
          collateralAsset,
          loanAmount: inputLoanAmount,
          collateralAmount: inputCollateralAmount,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "position_manager"
        });
        if (params) {
          trackMarketBorrowCompleted(params);
        }
        setInputCollateralAmount(ZERO);
        setInputLoanAmount(ZERO);
      }} entryPoint="position_manager" />, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[99] = account.isContract;
    $[100] = account.isSafe;
    $[101] = collateralAsset;
    $[102] = dialogData?.avgApy;
    $[103] = dialogData?.marketForRewards;
    $[104] = inputCollateralAmount;
    $[105] = inputLoanAmount;
    $[106] = loanAsset;
    $[107] = marketId;
    $[108] = marketParams;
    $[109] = nativeAmount;
    $[110] = reallocations;
    $[111] = shouldBlockFinalize;
    $[112] = targetChain.id;
    $[113] = targetChain.name;
    $[114] = toggleDialog;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  const handleFinalize = t31;
  if (queryIsLoading) {
    let t32;
    if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <PositionManagerBorrowDialogLoading />;
      $[116] = t32;
    } else {
      t32 = $[116];
    }
    return t32;
  }
  if (queryError || positionError && !positionData || !loanAsset || !collateralAsset) {
    let t32;
    if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <ErrorServiceUnavailable minHeight="400px" />;
      $[117] = t32;
    } else {
      t32 = $[117];
    }
    return t32;
  }
  const t32 = `Supply collateral ${collateralAsset.symbol}`;
  let t33;
  if ($[118] !== collateralAsset || $[119] !== inputCollateralAmount || $[120] !== isMaxCollateral || $[121] !== isMaxCollateralLoading || $[122] !== maxCollateral || $[123] !== onMaxCollateralClick || $[124] !== t32) {
    t33 = <CompactAssetInput header={t32} amount={inputCollateralAmount} token={collateralAsset} onChange={setInputCollateralAmount} onPendingChange={setIsCollateralInputPending} maxValue={maxCollateral} onMaxClick={onMaxCollateralClick} isMax={isMaxCollateral} loading={isMaxCollateralLoading} autoFocus={true} />;
    $[118] = collateralAsset;
    $[119] = inputCollateralAmount;
    $[120] = isMaxCollateral;
    $[121] = isMaxCollateralLoading;
    $[122] = maxCollateral;
    $[123] = onMaxCollateralClick;
    $[124] = t32;
    $[125] = t33;
  } else {
    t33 = $[125];
  }
  let t34;
  if ($[126] !== collateralAsset.symbol || $[127] !== isMarketSupplyBorrowEnabled || $[128] !== isWNativeCollateral || $[129] !== isWrapping || $[130] !== isWrappingToggleLoading || $[131] !== onWrappingChange || $[132] !== targetChain.nativeCurrency.symbol || $[133] !== wrappableNativeBalance) {
    t34 = <WrapSegment id="market-borrow" enabled={isMarketSupplyBorrowEnabled} isWNative={isWNativeCollateral} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={collateralAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[126] = collateralAsset.symbol;
    $[127] = isMarketSupplyBorrowEnabled;
    $[128] = isWNativeCollateral;
    $[129] = isWrapping;
    $[130] = isWrappingToggleLoading;
    $[131] = onWrappingChange;
    $[132] = targetChain.nativeCurrency.symbol;
    $[133] = wrappableNativeBalance;
    $[134] = t34;
  } else {
    t34 = $[134];
  }
  let t35;
  if ($[135] !== canTransferCollateralAsset || $[136] !== collateralAsset.address) {
    t35 = !canTransferCollateralAsset && <TransferError tokenAddress={collateralAsset.address} />;
    $[135] = canTransferCollateralAsset;
    $[136] = collateralAsset.address;
    $[137] = t35;
  } else {
    t35 = $[137];
  }
  let t36;
  if ($[138] !== t33 || $[139] !== t34 || $[140] !== t35) {
    t36 = <div className="flex flex-col gap-[2px]">{t33}{t34}{t35}</div>;
    $[138] = t33;
    $[139] = t34;
    $[140] = t35;
    $[141] = t36;
  } else {
    t36 = $[141];
  }
  const t37 = `Borrow ${loanAsset.symbol}`;
  let t38;
  if ($[142] !== inputLoanAmount || $[143] !== isMaxBorrow || $[144] !== isMaxBorrowLoading || $[145] !== loanAsset || $[146] !== maxBorrow || $[147] !== onMaxBorrowClick || $[148] !== t37) {
    t38 = <CompactAssetInput header={t37} amount={inputLoanAmount} token={loanAsset} onChange={setInputLoanAmount} onPendingChange={setIsLoanInputPending} maxValue={maxBorrow} onMaxClick={onMaxBorrowClick} isMax={isMaxBorrow} loading={isMaxBorrowLoading} />;
    $[142] = inputLoanAmount;
    $[143] = isMaxBorrow;
    $[144] = isMaxBorrowLoading;
    $[145] = loanAsset;
    $[146] = maxBorrow;
    $[147] = onMaxBorrowClick;
    $[148] = t37;
    $[149] = t38;
  } else {
    t38 = $[149];
  }
  let t39;
  if ($[150] !== canTransferLoanAsset || $[151] !== loanAsset.address) {
    t39 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[150] = canTransferLoanAsset;
    $[151] = loanAsset.address;
    $[152] = t39;
  } else {
    t39 = $[152];
  }
  let t40;
  if ($[153] !== t38 || $[154] !== t39) {
    t40 = <div className="flex flex-col gap-[2px]">{t38}{t39}</div>;
    $[153] = t38;
    $[154] = t39;
    $[155] = t40;
  } else {
    t40 = $[155];
  }
  const t41 = current?.collateral;
  const t42 = current?.borrowAssets;
  let t43;
  if ($[156] !== current) {
    t43 = current ? computeLtv({
      collateral: current.collateral,
      borrowAssets: current.borrowAssets,
      price: current.price
    }) : undefined;
    $[156] = current;
    $[157] = t43;
  } else {
    t43 = $[157];
  }
  let t44;
  if ($[158] !== t41 || $[159] !== t42 || $[160] !== t43) {
    t44 = {
      collateral: t41,
      loan: t42,
      ltv: t43
    };
    $[158] = t41;
    $[159] = t42;
    $[160] = t43;
    $[161] = t44;
  } else {
    t44 = $[161];
  }
  const t45 = marketParams?.lltv;
  const t46 = dialogData?.marketForRewards;
  const t47 = dialogData?.avgApy;
  let t48;
  if ($[162] !== after || $[163] !== collateralAsset || $[164] !== isPositionLoading || $[165] !== loanAsset || $[166] !== marketId || $[167] !== t44 || $[168] !== t45 || $[169] !== t46 || $[170] !== t47) {
    t48 = <MarketSummaryCard marketId={marketId} current={t44} after={after} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={t45} isLoading={isPositionLoading} marketForRewards={t46} avgBorrowApy={t47} background="muted" />;
    $[162] = after;
    $[163] = collateralAsset;
    $[164] = isPositionLoading;
    $[165] = loanAsset;
    $[166] = marketId;
    $[167] = t44;
    $[168] = t45;
    $[169] = t46;
    $[170] = t47;
    $[171] = t48;
  } else {
    t48 = $[171];
  }
  let t49;
  if ($[172] !== isBundleNotAuthorized) {
    t49 = isBundleNotAuthorized && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[172] = isBundleNotAuthorized;
    $[173] = t49;
  } else {
    t49 = $[173];
  }
  const t50 = isCollateralInputPending || isLoanInputPending;
  let t51;
  if ($[174] !== geoActionProps || $[175] !== handleFinalize || $[176] !== isDebouncing || $[177] !== t50) {
    t51 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={t50} isValidating={isDebouncing} />;
    $[174] = geoActionProps;
    $[175] = handleFinalize;
    $[176] = isDebouncing;
    $[177] = t50;
    $[178] = t51;
  } else {
    t51 = $[178];
  }
  let t52;
  if ($[179] !== t36 || $[180] !== t40 || $[181] !== t48 || $[182] !== t49 || $[183] !== t51) {
    t52 = <>{t36}{t40}{t48}{t49}{t51}</>;
    $[179] = t36;
    $[180] = t40;
    $[181] = t48;
    $[182] = t49;
    $[183] = t51;
    $[184] = t52;
  } else {
    t52 = $[184];
  }
  return t52;
}
export default memo(PositionManagerBorrowDialog);